import {
  ApiError,
  createMembershipPlan,
  listMembershipPlans,
  updateMembershipPlan,
} from '@jq4race/api-client';
import { LocalizedError, useTranslation } from '@jq4race/i18n';
import type { CreateMembershipPlanRequest, MembershipPlanResponse } from '@jq4race/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  formatCents,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@jq4race/ui';
import {
  CheckCircle2,
  CreditCard,
  Pencil,
  Plus,
  Power,
  PowerOff,
  TriangleAlert,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { AccessDeniedState } from '../../../../shared/errors/AccessDeniedState';
import { isForbiddenError } from '../../../../shared/errors/isForbiddenError';
import { RestrictedMeta } from '../../../../shared/errors/RestrictedMeta';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { useAutoDismissFeedback } from '../../../../shared/hooks/useAutoDismissFeedback';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { MembershipPlanForm } from '../components/MembershipPlanForm';
import { TogglePlanActiveDialog } from '../components/TogglePlanActiveDialog';

export function MembershipPlansPage() {
  const { t } = useTranslation('memberships');
  const [plans, setPlans] = useState<MembershipPlanResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // See EventsPage.tsx for the confirmed production incident this guards
  // against: a 403 (role changed mid-session) must not render like "no
  // plans", and its banner must not auto-dismiss.
  const [accessDenied, setAccessDenied] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<MembershipPlanResponse | 'new' | null>(null);
  const [toggleTarget, setToggleTarget] = useState<MembershipPlanResponse | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAccessDenied(false);
    try {
      const res = await listMembershipPlans();
      setPlans(res.plans);
    } catch (e) {
      setAccessDenied(isForbiddenError(e));
      setError(translateApiError(e, t, { fallback: t('membershipPlansPage.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useAutoDismissFeedback({
    error,
    success,
    isErrorPersistent: accessDenied,
    onDismissError: () => setError(null),
    onDismissSuccess: () => setSuccess(null),
  });

  async function handleFormSubmit(values: CreateMembershipPlanRequest) {
    try {
      if (editTarget && editTarget !== 'new') {
        await updateMembershipPlan(editTarget.id, values);
        setSuccess(t('membershipPlansPage.updated'));
      } else {
        await createMembershipPlan(values);
        setSuccess(t('membershipPlansPage.created'));
      }
      setEditTarget(null);
      void reload();
    } catch (e) {
      if (e instanceof ApiError)
        throw new LocalizedError(
          translateApiError(e, t, { fallback: t('membershipPlansPage.saveError') }),
        );
      throw e;
    }
  }

  async function handleToggleConfirm() {
    if (!toggleTarget) return;
    try {
      await updateMembershipPlan(toggleTarget.id, { active: !toggleTarget.active });
      setSuccess(
        t('membershipPlansPage.toggled', {
          name: toggleTarget.name,
          action: toggleTarget.active
            ? t('membershipPlansPage.deactivatedWord')
            : t('membershipPlansPage.reactivatedWord'),
        }),
      );
      void reload();
    } catch (e) {
      if (e instanceof ApiError) {
        setError(translateApiError(e, t, { fallback: t('membershipPlansPage.operationFailed') }));
        return;
      }
      setError(translateApiError(e, t, { fallback: t('membershipPlansPage.operationFailed') }));
    }
  }

  const formOpen = editTarget !== null;
  const formMode = editTarget === 'new' ? 'create' : 'edit';
  const formDefaults =
    editTarget && editTarget !== 'new'
      ? {
          name: editTarget.name,
          interval: editTarget.interval,
          intervalCount: editTarget.intervalCount,
          priceCents: editTarget.priceCents / 100,
          currency: editTarget.currency,
          graceDays: editTarget.graceDays,
          maxRetries: editTarget.maxRetries,
          retryIntervalHours: editTarget.retryIntervalHours,
        }
      : undefined;

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('membershipPlansPage.eyebrow')}
        title={t('membershipPlansPage.title')}
        description={t('membershipPlansPage.description')}
        meta={
          <p className="text-[13px] text-jq-graphite">
            <RestrictedMeta loading={loading && plans.length === 0} accessDenied={accessDenied}>
              <span className="font-medium tabular-nums text-jq-charcoal">{plans.length}</span>{' '}
              {t('membershipPlansPage.count', { count: plans.length })}
            </RestrictedMeta>
          </p>
        }
        actions={
          <Button size="sm" onClick={() => setEditTarget('new')}>
            <Plus className="h-4 w-4" strokeWidth={1.75} />
            {t('membershipPlansPage.newPlan')}
          </Button>
        }
      />

      <FeedbackBanner tone="error" message={error} onDismiss={() => setError(null)} />
      <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          {accessDenied ? (
            <AccessDeniedState />
          ) : (
            <PlansTable
              plans={plans}
              loading={loading}
              onEdit={setEditTarget}
              onToggle={setToggleTarget}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={formOpen} onOpenChange={(next) => (!next ? setEditTarget(null) : undefined)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {formMode === 'create'
                ? t('membershipPlansPage.newPlanDialogTitle')
                : t('membershipPlansPage.editPlanDialogTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-6">
            <MembershipPlanForm
              mode={formMode}
              defaultValues={formDefaults}
              onSubmit={handleFormSubmit}
              onCancel={() => setEditTarget(null)}
              submitLabel={
                formMode === 'create'
                  ? t('membershipPlansPage.createPlan')
                  : t('membershipPlansPage.saveChanges')
              }
            />
          </div>
        </DialogContent>
      </Dialog>

      <TogglePlanActiveDialog
        plan={toggleTarget}
        onConfirm={handleToggleConfirm}
        onCancel={() => setToggleTarget(null)}
      />
    </div>
  );
}

// ── Table ──────────────────────────────────────────────────────────────────

interface PlansTableProps {
  plans: MembershipPlanResponse[];
  loading: boolean;
  onEdit: (plan: MembershipPlanResponse) => void;
  onToggle: (plan: MembershipPlanResponse) => void;
}

function PlansTable({ plans, loading, onEdit, onToggle }: PlansTableProps) {
  const { t } = useTranslation('memberships');
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('membershipPlansPage.table.name')}</TableHead>
          <TableHead>{t('membershipPlansPage.table.interval')}</TableHead>
          <TableHead>{t('membershipPlansPage.table.price')}</TableHead>
          <TableHead>{t('membershipPlansPage.table.status')}</TableHead>
          <TableHead className="w-[100px] text-right">
            {t('membershipPlansPage.table.actions')}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && plans.length === 0 ? (
          <SkeletonRows />
        ) : plans.length === 0 ? (
          <TableEmpty colSpan={5}>
            <EmptyState />
          </TableEmpty>
        ) : (
          plans.map((plan) => (
            <TableRow key={plan.id}>
              <TableCell>
                <span className="font-medium text-jq-charcoal">{plan.name}</span>
              </TableCell>
              <TableCell className="text-jq-graphite">
                {t('membershipPlansPage.table.every', {
                  count: plan.intervalCount,
                  unit: t(`membershipPlansPage.table.intervalUnit.${plan.interval}`, {
                    count: plan.intervalCount,
                  }),
                })}
              </TableCell>
              <TableCell className="text-jq-graphite">
                {formatCents(plan.priceCents, plan.currency)}
              </TableCell>
              <TableCell>
                <Badge variant={plan.active ? 'success' : 'secondary'}>
                  {plan.active
                    ? t('membershipPlansPage.table.active')
                    : t('membershipPlansPage.table.inactive')}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => onEdit(plan)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded text-jq-graphite transition hover:bg-jq-surface-2 hover:text-jq-navy"
                    aria-label={t('membershipPlansPage.table.edit', { name: plan.name })}
                  >
                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onToggle(plan)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded text-jq-graphite transition hover:bg-jq-surface-2 hover:text-jq-navy"
                    aria-label={
                      plan.active
                        ? t('membershipPlansPage.table.deactivate', { name: plan.name })
                        : t('membershipPlansPage.table.reactivate', { name: plan.name })
                    }
                  >
                    {plan.active ? (
                      <PowerOff className="h-3.5 w-3.5" strokeWidth={1.75} />
                    ) : (
                      <Power className="h-3.5 w-3.5" strokeWidth={1.75} />
                    )}
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
        <TableRow key={`skeleton-${i}`}>
          {Array.from({ length: 5 }).map((_, j) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
            <TableCell key={`cell-${j}`}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function EmptyState() {
  const { t } = useTranslation('memberships');
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <CreditCard className="h-10 w-10 text-jq-stone" strokeWidth={1.25} />
      <p className="text-[14px] font-medium text-jq-charcoal">
        {t('membershipPlansPage.empty.title')}
      </p>
      <p className="max-w-sm text-[13px] text-jq-graphite">
        {t('membershipPlansPage.empty.description')}
      </p>
    </div>
  );
}

// ── Feedback banner ────────────────────────────────────────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('memberships');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
